import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError, API_BASE } from "../api/client";
import { useI18n, formatDate, formatDateTime, formatMoney } from "../i18n";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, Card, Badge, EmptyState } from "../components/shared";
import {
  Package, Plus, X, AlertTriangle, PackageX, Clock, Trash2, Pill,
  ScanLine, Upload, Download, Search, TrendingDown, Zap, Barcode,
  ArrowDownToLine, ArrowUpFromLine, FileSpreadsheet, User, Camera,
} from "lucide-react";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";

const INPUT =
  "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-12 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";

export default function Stock() {
  const { t } = useI18n();
  const { user } = useAuth();
  const canManage = ["owner", "stock_manager"].includes(user.role);
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [lots, setLots] = useState([]);
  const [movements, setMovements] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [dash, setDash] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [dlg, setDlg] = useState(null); // 'product' | 'lot' | 'movement'
  const [form, setForm] = useState({});
  const [scanOpen, setScanOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // Quick entry state
  const [qeProductId, setQeProductId] = useState("");
  const [qeQty, setQeQty] = useState("");

  const loadAll = async () => {
    const [p, l, m, a, d] = await Promise.all([
      api.get("/stock/products"),
      api.get("/stock/lots"),
      api.get("/stock/movements", { params: { limit: 200 } }),
      api.get("/stock/alerts"),
      api.get("/stock/dashboard"),
    ]);
    setProducts(p.data); setLots(l.data); setMovements(m.data); setAlerts(a.data); setDash(d.data);
  };

  useEffect(() => { loadAll(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDlg = (kind, preset = {}) => {
    if (kind === "product") setForm({ name: "", category: "", unit: "unit", purchase_price: 0, sale_price: 0, minimum_threshold: 0, barcode: "", ...preset });
    if (kind === "lot") setForm({ product_id: "", batch_number: "", expiration_date: "", supplier: "", quantity: 0, ...preset });
    if (kind === "movement") setForm({ product_id: "", type: "IN", subtype: "purchase", quantity: 0, reason: "", ...preset });
    setDlg(kind);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (dlg === "product") {
        await api.post("/stock/products", {
          ...form,
          purchase_price: parseFloat(form.purchase_price) || 0,
          sale_price: parseFloat(form.sale_price) || 0,
          minimum_threshold: parseInt(form.minimum_threshold) || 0,
        });
      } else if (dlg === "lot") {
        await api.post("/stock/lots", { ...form, quantity: parseInt(form.quantity) || 0 });
      } else if (dlg === "movement") {
        await api.post("/stock/movements", { ...form, quantity: parseInt(form.quantity) || 0 });
      }
      toast.success(t("common.success"));
      setDlg(null);
      loadAll();
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  const quickAdd = async () => {
    const qty = parseInt(qeQty) || 0;
    if (!qeProductId || qty <= 0) {
      toast.error(t("common.error"));
      return;
    }
    try {
      await api.post("/stock/movements", {
        product_id: qeProductId,
        type: "IN",
        subtype: "purchase",
        quantity: qty,
        reason: "Ajout rapide",
      });
      toast.success(t("common.success"));
      setQeProductId(""); setQeQty("");
      loadAll();
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  const handleBarcodeDecoded = async (code) => {
    setScanOpen(false);
    try {
      const { data } = await api.get("/stock/products/lookup", { params: { barcode: code } });
      setQeProductId(data.id);
      toast.success(`${data.name} — ${t("common.success")}`);
    } catch (err) {
      // Not found — prompt to create
      if (window.confirm(t("stock.scanNotFound"))) {
        openDlg("product", { barcode: code });
      }
    }
  };

  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products;
    const s = productSearch.toLowerCase();
    return products.filter((p) =>
      (p.name || "").toLowerCase().includes(s) ||
      (p.category || "").toLowerCase().includes(s) ||
      (p.barcode || "").toLowerCase().includes(s)
    );
  }, [products, productSearch]);

  const tabs = [
    { key: "products", label: t("stock.products"), icon: Package, count: products.length },
    { key: "lots", label: t("stock.lots"), icon: Pill, count: lots.length },
    { key: "movements", label: t("stock.movements"), icon: Clock, count: movements.length },
    { key: "alerts", label: t("stock.alerts"), icon: AlertTriangle,
      count: alerts ? (alerts.out_of_stock.length + alerts.low_stock.length + alerts.expiring_soon.length + alerts.expired.length) : 0,
      tone: "danger",
    },
  ];

  return (
    <div>
      <PageHeader
        title={t("stock.title")}
        actions={canManage && (
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setScanOpen(true)} data-testid="scan-btn"
              className="h-12 px-4 rounded-xl bg-[#151E28] hover:bg-black text-white font-heading font-bold flex items-center gap-2">
              <ScanLine className="w-4 h-4" /> {t("stock.scan")}
            </button>
            <button onClick={() => setImportOpen(true)} data-testid="import-btn"
              className="h-12 px-4 rounded-xl bg-white border-2 border-brand text-brand font-heading font-bold flex items-center gap-2">
              <Upload className="w-4 h-4" /> {t("stock.importExcel")}
            </button>
            <button onClick={() => openDlg("product")} data-testid="new-product-btn"
              className="h-12 px-4 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold flex items-center gap-2">
              <Plus className="w-4 h-4" /> {t("stock.newProduct")}
            </button>
            <button onClick={() => openDlg("lot")} data-testid="new-lot-btn"
              className="h-12 px-4 rounded-xl bg-terracotta hover:bg-terracotta-hover text-white font-heading font-bold flex items-center gap-2">
              <Plus className="w-4 h-4" /> {t("stock.newLot")}
            </button>
          </div>
        )}
      />

      {/* KPI bar */}
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="stock-kpis">
          <KpiCard testid="kpi-total" icon={Package} label={t("stock.totalProducts")} value={dash.total_products} tone="brand" />
          <KpiCard testid="kpi-low" icon={AlertTriangle} label={t("stock.lowStock")} value={dash.low_stock} tone={dash.low_stock > 0 ? "warning" : "neutral"} />
          <KpiCard testid="kpi-out" icon={PackageX} label={t("stock.outOfStock")} value={dash.out_of_stock} tone={dash.out_of_stock > 0 ? "danger" : "neutral"} />
          <KpiCard testid="kpi-expiring" icon={Clock} label={t("stock.expiringSoon")} value={dash.expiring_soon} tone={dash.expiring_soon > 0 ? "warning" : "neutral"} />
          <KpiCard testid="kpi-expired" icon={Trash2} label={t("stock.expired")} value={dash.expired} tone={dash.expired > 0 ? "danger" : "neutral"} />
        </div>
      )}

      {/* Quick entry + Prediction strip */}
      {canManage && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <Card className="lg:col-span-2 p-5" data-testid="quick-entry">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-brand" />
              <h3 className="font-heading font-bold text-ink">{t("stock.quickEntry")}</h3>
            </div>
            <p className="text-xs text-ink-muted mb-3">{t("stock.quickEntryHint")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
              <button onClick={() => setScanOpen(true)} data-testid="qe-scan-btn"
                className="sm:col-span-2 h-12 rounded-xl bg-[#151E28] text-white font-semibold flex items-center justify-center gap-1">
                <Barcode className="w-4 h-4" /> {t("stock.scan")}
              </button>
              <select data-testid="qe-product" className={`${INPUT} sm:col-span-5`}
                value={qeProductId} onChange={(e) => setQeProductId(e.target.value)}>
                <option value="">— {t("stock.productName")}</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.barcode ? ` · ${p.barcode}` : ""} (stock: {p.stock_quantity})
                  </option>
                ))}
              </select>
              <input type="number" min={1} data-testid="qe-qty"
                placeholder={t("stock.quantity")}
                className={`${INPUT} sm:col-span-2`}
                value={qeQty} onChange={(e) => setQeQty(e.target.value)} />
              <button onClick={quickAdd} data-testid="qe-add-btn"
                className="sm:col-span-3 h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" /> {t("stock.quickAdd")}
              </button>
            </div>
          </Card>
          <Card className="p-5" data-testid="predicted-card">
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown className="w-5 h-5 text-[#8a5a10]" />
              <h3 className="font-heading font-bold text-ink">{t("stock.predictedDepletion")}</h3>
            </div>
            {!dash || dash.predicted_depletion.length === 0 ? (
              <div className="text-xs text-ink-muted">{t("stock.noCriticalSoon")}</div>
            ) : (
              <ul className="space-y-1.5">
                {dash.predicted_depletion.slice(0, 5).map((p) => (
                  <li key={p.product_id} className="flex items-center justify-between text-sm">
                    <span className="text-ink truncate">{p.name}</span>
                    <span className={`stat-number font-bold shrink-0 ml-2 ${p.days_left <= 7 ? "text-[#b12e31]" : p.days_left <= 30 ? "text-[#8a5a10]" : "text-ink-muted"}`}>
                      {t("stock.daysLeft").replace("{n}", Math.ceil(p.days_left))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-4 overflow-x-auto">
        <div className="flex gap-2 p-1 rounded-2xl bg-subtle w-fit">
          {tabs.map((t_) => (
            <button key={t_.key} onClick={() => setTab(t_.key)}
              data-testid={`tab-${t_.key}`}
              className={`h-11 px-4 rounded-xl font-semibold flex items-center gap-2 transition-colors ${tab === t_.key ? "bg-white shadow-sm text-brand" : "text-ink-muted hover:text-ink"}`}>
              <t_.icon className="w-4 h-4" /> {t_.label}
              {t_.count > 0 && <span className={`text-xs px-2 py-0.5 rounded-full ${t_.tone === "danger" ? "bg-[#d34044]/10 text-[#b12e31]" : "bg-subtle text-ink-muted"}`}>{t_.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {tab === "products" && (
        <>
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input data-testid="prod-search" placeholder={t("common.search")} value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)} className={`${INPUT} pl-10`} />
          </div>
          <Card className="overflow-hidden">
            {filteredProducts.length === 0 ? <EmptyState icon={Package} title={t("common.noData")} /> : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-subtle text-xs uppercase text-ink-muted tracking-wider">
                      <th className="text-left py-3 px-5">{t("stock.productName")}</th>
                      <th className="text-left py-3 px-5">{t("stock.barcode")}</th>
                      <th className="text-left py-3 px-5">{t("stock.category")}</th>
                      <th className="text-right py-3 px-5">{t("stock.stockQty")}</th>
                      <th className="text-right py-3 px-5">{t("stock.minThreshold")}</th>
                      <th className="text-right py-3 px-5">{t("stock.salePrice")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((p) => {
                      const low = p.minimum_threshold > 0 && p.stock_quantity <= p.minimum_threshold;
                      const out = p.stock_quantity <= 0;
                      return (
                        <tr key={p.id} className="border-t border-[var(--border-default)] hover:bg-subtle/50" data-testid={`prod-${p.id}`}>
                          <td className="py-3 px-5 font-semibold">{p.name}</td>
                          <td className="py-3 px-5 font-mono text-xs text-ink-muted">{p.barcode || "—"}</td>
                          <td className="py-3 px-5 text-ink-muted">{p.category || "—"}</td>
                          <td className="py-3 px-5 text-right">
                            <span className="stat-number font-bold">{p.stock_quantity}</span>
                            <span className="text-ink-muted text-xs ml-1">{p.unit}</span>
                            {out && <Badge tone="danger" className="ml-2">{t("stock.outOfStock")}</Badge>}
                            {!out && low && <Badge tone="warning" className="ml-2">{t("stock.lowStock")}</Badge>}
                          </td>
                          <td className="py-3 px-5 text-right stat-number text-ink-muted">{p.minimum_threshold}</td>
                          <td className="py-3 px-5 text-right stat-number">{formatMoney(p.sale_price, "XOF")}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {tab === "lots" && (
        <Card className="overflow-hidden">
          <div className="text-xs text-ink-muted px-5 py-2 bg-subtle border-b border-[var(--border-default)]">
            <AlertTriangle className="w-3 h-3 inline mr-1" /> {t("stock.fifoNote")}
          </div>
          {lots.length === 0 ? <EmptyState icon={Pill} title={t("common.noData")} /> : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-subtle text-xs uppercase text-ink-muted tracking-wider">
                    <th className="text-left py-3 px-5">{t("stock.productName")}</th>
                    <th className="text-left py-3 px-5">{t("stock.batchNumber")}</th>
                    <th className="text-left py-3 px-5">{t("stock.expDate")}</th>
                    <th className="text-right py-3 px-5">{t("stock.quantity")}</th>
                    <th className="text-left py-3 px-5">{t("stock.supplier")}</th>
                  </tr>
                </thead>
                <tbody>
                  {lots.map((l) => {
                    const prod = products.find((p) => p.id === l.product_id);
                    const exp = new Date(l.expiration_date);
                    const days = Math.ceil((exp - Date.now()) / (1000 * 3600 * 24));
                    const expired = days < 0;
                    const urgent = !expired && days <= 7;
                    const soon = !expired && !urgent && days <= 30;
                    return (
                      <tr key={l.id} className={`border-t border-[var(--border-default)] ${expired ? "bg-[#d34044]/5" : urgent ? "bg-[#d34044]/5" : soon ? "bg-[#e3a03b]/10" : ""}`} data-testid={`lot-${l.id}`}>
                        <td className="py-3 px-5 font-semibold">{prod?.name || l.product_id}</td>
                        <td className="py-3 px-5 font-mono text-sm">{l.batch_number}</td>
                        <td className="py-3 px-5">
                          <span className={`stat-number ${expired || urgent ? "text-[#b12e31] font-bold" : soon ? "text-[#8a5a10]" : ""}`}>{formatDate(l.expiration_date)}</span>
                          {expired && <Badge tone="danger" className="ml-2">{t("stock.expired")}</Badge>}
                          {urgent && !expired && <Badge tone="danger" className="ml-2">J-{days}</Badge>}
                          {soon && <Badge tone="warning" className="ml-2">J-{days}</Badge>}
                        </td>
                        <td className="py-3 px-5 text-right stat-number font-bold">{l.remaining_qty}<span className="text-ink-muted text-xs">/{l.initial_qty}</span></td>
                        <td className="py-3 px-5 text-ink-muted">{l.supplier || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === "movements" && (
        <Card className="overflow-hidden">
          {movements.length === 0 ? <EmptyState icon={Clock} title={t("common.noData")} /> : (
            <ul className="divide-y divide-[var(--border-default)]">
              {movements.map((m) => (
                <MovementRow key={m.id} m={m} t={t} />
              ))}
            </ul>
          )}
        </Card>
      )}

      {tab === "alerts" && alerts && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AlertPanel tone="danger" icon={PackageX} title={t("stock.outOfStock")} items={alerts.out_of_stock}
            render={(p) => <span className="font-semibold">{p.name}</span>} testid="alert-out" />
          <AlertPanel tone="warning" icon={AlertTriangle} title={t("stock.lowStock")} items={alerts.low_stock}
            render={(p) => <><span className="font-semibold">{p.name}</span> <span className="stat-number text-ink-muted text-xs">({p.stock_quantity}/{p.minimum_threshold})</span></>} testid="alert-low" />
          <AlertPanel tone="warning" icon={Clock} title={t("stock.expiringSoon")} items={alerts.expiring_soon}
            render={(l) => {
              const prod = products.find((pp) => pp.id === l.product_id);
              return <><span className="font-semibold">{prod?.name || l.product_id}</span> · <span className="font-mono text-xs">{l.batch_number}</span> · <span className="text-[#8a5a10]">{formatDate(l.expiration_date)}</span></>;
            }} testid="alert-expiring" />
          <AlertPanel tone="danger" icon={Trash2} title={t("stock.expired")} items={alerts.expired}
            render={(l) => {
              const prod = products.find((pp) => pp.id === l.product_id);
              return <><span className="font-semibold">{prod?.name || l.product_id}</span> · <span className="font-mono text-xs">{l.batch_number}</span> · <span className="text-[#b12e31]">{formatDate(l.expiration_date)}</span></>;
            }} testid="alert-expired" />
        </div>
      )}

      {/* Modals */}
      {scanOpen && <BarcodeScanner onClose={() => setScanOpen(false)} onDecoded={handleBarcodeDecoded} t={t} />}
      {importOpen && <ImportModal onClose={() => setImportOpen(false)} onDone={loadAll} t={t} />}
      {dlg && (
        <FormDialog
          kind={dlg} form={form} setForm={setForm} products={products}
          onClose={() => setDlg(null)} onSubmit={submit} t={t}
        />
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function KpiCard({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    neutral: "bg-subtle text-ink",
    brand: "bg-brand/10 text-brand",
    success: "bg-[#2d8a56]/10 text-[#2d8a56]",
    warning: "bg-[#e3a03b]/15 text-[#8a5a10]",
    danger: "bg-[#d34044]/10 text-[#b12e31]",
  };
  return (
    <Card className="p-4 flex items-start gap-3" data-testid={testid}>
      <div className={`p-2.5 rounded-xl ${tones[tone] || tones.neutral}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] text-ink-muted uppercase tracking-wider">{label}</div>
        <div className="stat-number text-2xl font-heading font-extrabold text-ink">{value}</div>
      </div>
    </Card>
  );
}

function AlertPanel({ icon: Icon, title, items, render, tone = "warning", testid }) {
  const color = tone === "danger" ? "text-[#b12e31]" : "text-[#8a5a10]";
  return (
    <Card className="p-5" data-testid={testid}>
      <h3 className="font-heading font-bold text-ink mb-3 flex items-center gap-2">
        <Icon className={`w-5 h-5 ${color}`} /> {title}
        <span className="ml-auto text-sm text-ink-muted">{items.length}</span>
      </h3>
      {items.length === 0 ? <div className="text-ink-muted text-sm">Aucun élément</div> : (
        <ul className="space-y-1.5 text-sm">
          {items.slice(0, 20).map((it, i) => <li key={it.id || i}>{render(it)}</li>)}
        </ul>
      )}
    </Card>
  );
}

function MovementRow({ m, t }) {
  const isIn = m.type === "IN";
  const prod = m.product || {};
  const u = m.user || {};
  const pat = m.patient || null;
  const subLabel = t(`stock.${m.subtype}`) || m.subtype;
  return (
    <li className="p-4 flex items-center gap-4" data-testid={`mv-${m.id}`}>
      <div className={`p-2 rounded-lg ${isIn ? "bg-[#2d8a56]/10 text-[#2d8a56]" : "bg-[#d34044]/10 text-[#b12e31]"}`}>
        {isIn ? <ArrowDownToLine className="w-4 h-4" /> : <ArrowUpFromLine className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-ink truncate">{prod.name || m.product_id}</div>
        <div className="text-xs text-ink-muted flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span>{subLabel}</span>
          {m.reason && <span>· {m.reason}</span>}
          {pat && <span>· {t("stock.consumedBy")}: {pat.first_name} {pat.last_name}</span>}
          {u.first_name && <span>· {t("stock.recordedBy")}: {u.first_name} {u.last_name}</span>}
          <span>· {formatDateTime(m.created_at)}</span>
        </div>
      </div>
      <div className={`stat-number font-bold shrink-0 ${isIn ? "text-[#2d8a56]" : "text-[#b12e31]"}`}>
        {isIn ? "+" : "−"}{m.quantity}
        <span className="text-ink-muted text-xs ml-1">{prod.unit || ""}</span>
      </div>
    </li>
  );
}

function BarcodeScanner({ onClose, onDecoded, t }) {
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");
  const scannerRef = useRef(null);
  const containerId = "qr-scanner-region";

  useEffect(() => {
    const scanner = new Html5Qrcode(containerId);
    scannerRef.current = scanner;
    const config = { fps: 10, qrbox: { width: 280, height: 160 } };
    scanner
      .start({ facingMode: "environment" }, config, (decodedText) => {
        onDecoded(decodedText);
      }, () => {})
      .catch((e) => setError(e?.message || "camera error"));
    return () => {
      try {
        if (scannerRef.current?.isScanning) {
          scannerRef.current.stop().then(() => scannerRef.current.clear()).catch(() => {});
        }
      } catch {}
    };
  }, [onDecoded]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/80" data-testid="scan-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-default)]">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <Camera className="w-5 h-5 text-brand" /> {t("stock.scan")}
          </h3>
          <button onClick={onClose} data-testid="scan-close" className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4">
          {error ? (
            <div className="rounded-xl bg-[#d34044]/10 text-[#b12e31] p-4 text-sm mb-3">
              {t("stock.scanPermission")}
              <div className="text-xs mt-1 opacity-80">{error}</div>
            </div>
          ) : (
            <>
              <div id={containerId} className="rounded-xl overflow-hidden bg-black" style={{ minHeight: 260 }} data-testid="scan-region" />
              <div className="text-xs text-ink-muted mt-2 text-center">{t("stock.scanHint")}</div>
            </>
          )}
          {/* Manual fallback */}
          <div className="mt-4 pt-4 border-t border-[var(--border-default)]">
            <label className="text-sm font-semibold">{t("stock.barcode")}</label>
            <div className="flex gap-2 mt-2">
              <input data-testid="scan-manual-input" className={INPUT}
                placeholder="..." value={manual} onChange={(e) => setManual(e.target.value)} />
              <button data-testid="scan-manual-submit" onClick={() => manual && onDecoded(manual)}
                className="h-12 px-4 rounded-xl bg-brand text-white font-semibold">OK</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ onClose, onDone, t }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // { detected, sample, warnings, total_rows, canonical_columns }
  const [overrides, setOverrides] = useState({}); // { raw_header: canonical_col }
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const previewFile = async (f) => {
    if (!f) return;
    setLoading(true);
    setResult(null);
    setPreview(null);
    setOverrides({});
    try {
      const fd = new FormData();
      fd.append("file", f);
      const token = localStorage.getItem("mediflow_token");
      const res = await fetch(`${API_BASE}/stock/products/import/preview`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "error" }));
        throw new Error(err.detail || "Preview failed");
      }
      const data = await res.json();
      setPreview(data);
      // Seed overrides with detected mapping so user can tweak
      const seed = {};
      data.detected.forEach((d) => { seed[d.raw] = d.canonical || ""; });
      setOverrides(seed);
    } catch (err) {
      toast.error(err.message || "error");
    } finally {
      setLoading(false);
    }
  };

  const confirm = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("header_map", JSON.stringify(overrides));
      const token = localStorage.getItem("mediflow_token");
      const res = await fetch(`${API_BASE}/stock/products/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "error" }));
        throw new Error(err.detail || "Import failed");
      }
      const data = await res.json();
      setResult(data);
      if (data.created > 0 || data.updated > 0) {
        toast.success(t("stock.imported").replace("{created}", data.created).replace("{updated}", data.updated));
        onDone({ keepOpen: true });
      } else {
        toast.warning(`Aucun produit importé — ${data.total || 0} lignes lues`);
      }
    } catch (err) {
      toast.error(err.message || "error");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setPreview(null); setResult(null); setFile(null); setOverrides({}); };

  const downloadTemplate = () => {
    const csv = "nom,catégorie,unité,prix_achat,prix_vente,seuil_min,code_barres,stock\nParacétamol 500mg,Antalgique,boîte,500,800,10,3400934445001,50\nAmoxicilline 250mg,Antibiotique,boîte,1200,1800,5,,20\n";
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mediflow_stock_template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // Human-readable labels for canonical columns (for the dropdown)
  const canonicalLabels = {
    name: "Nom", category: "Catégorie", unit: "Unité",
    purchase_price: "Prix d'achat", sale_price: "Prix de vente",
    minimum_threshold: "Seuil minimum", barcode: "Code-barres", quantity: "Stock / Quantité",
  };

  const mappedCanonicals = Object.values(overrides).filter(Boolean);
  const hasName = mappedCanonicals.includes("name");

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50" data-testid="import-modal">
      <div className={`bg-white rounded-t-3xl sm:rounded-2xl w-full ${preview ? "max-w-3xl" : "max-w-md"} max-h-[92vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-default)] sticky top-0 bg-white z-10">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-brand" /> {t("stock.importExcel")}
          </h3>
          <button onClick={onClose} data-testid="import-close" className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
        </div>

        {/* STEP 1 — file picker */}
        {!preview && !result && (
          <div className="p-5 space-y-4">
            <div className="text-xs text-ink-muted">{t("stock.importHint")}</div>
            <button type="button" onClick={downloadTemplate} data-testid="import-template"
              className="inline-flex items-center gap-1 text-sm text-brand font-semibold">
              <Download className="w-4 h-4" /> {t("stock.importTemplate")}
            </button>
            <div>
              <label className="block mb-2 text-sm font-semibold">{t("stock.selectFile")}</label>
              <input type="file" accept=".xlsx,.csv" data-testid="import-file"
                className="w-full text-sm"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setFile(f);
                  if (f) previewFile(f);
                }} />
            </div>
            {loading && <div className="text-sm text-ink-muted">Analyse du fichier…</div>}
          </div>
        )}

        {/* STEP 2 — preview with override */}
        {preview && !result && (
          <div className="p-5 space-y-4" data-testid="import-preview">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="font-semibold">{file?.name}</span>
                <span className="text-ink-muted"> · {preview.total_rows} ligne(s) détectée(s)</span>
              </div>
              <button onClick={reset} className="text-xs text-ink-muted hover:text-brand underline">Changer de fichier</button>
            </div>

            {!hasName && (
              <div className="text-xs text-[#b12e31] bg-[#d34044]/10 p-3 rounded-lg">
                ⚠ Aucune colonne n'est mappée vers <b>Nom</b>. Mappez au moins une colonne ci-dessous avant de confirmer.
              </div>
            )}

            <div>
              <div className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-2">Colonnes détectées</div>
              <div className="space-y-2">
                {preview.detected.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm" data-testid={`import-col-${i}`}>
                    <div className="flex-1 font-mono text-xs bg-subtle px-2 py-1.5 rounded-lg truncate" title={d.raw}>
                      {d.raw || <span className="text-ink-muted italic">(vide)</span>}
                    </div>
                    <span className="text-ink-muted">→</span>
                    <select
                      value={overrides[d.raw] || ""}
                      onChange={(e) => setOverrides({ ...overrides, [d.raw]: e.target.value })}
                      data-testid={`import-col-select-${i}`}
                      className="flex-1 h-9 rounded-lg border border-[var(--border-default)] bg-white px-2 text-sm focus:border-brand outline-none">
                      <option value="">— Ignorer —</option>
                      {preview.canonical_columns.map((c) => (
                        <option key={c} value={c}>{canonicalLabels[c] || c}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {preview.sample?.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-2">Aperçu ({preview.sample.length} premières lignes)</div>
                <div className="overflow-x-auto rounded-lg border border-[var(--border-default)]">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-subtle">
                        {preview.canonical_columns.map((c) => (
                          <th key={c} className="text-left px-2 py-1.5 font-semibold text-ink-muted uppercase">{canonicalLabels[c] || c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sample.map((row, i) => (
                        <tr key={i} className="border-t border-[var(--border-default)]">
                          {preview.canonical_columns.map((c) => (
                            <td key={c} className="px-2 py-1.5 text-ink whitespace-nowrap">{row[c] ?? <span className="text-ink-faint">—</span>}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-[10px] text-ink-muted mt-1 italic">Ajustez les colonnes ci-dessus si l'aperçu ne reflète pas vos données.</div>
              </div>
            )}

            <div className="flex gap-2 sticky bottom-0 bg-white pt-3 border-t border-[var(--border-default)]">
              <button onClick={reset} className="flex-1 h-12 rounded-xl border border-[var(--border-default)] font-semibold">
                Annuler
              </button>
              <button onClick={confirm} disabled={loading || !hasName} data-testid="import-submit"
                className="flex-1 h-12 rounded-xl bg-brand hover:bg-brand-hover disabled:opacity-50 text-white font-heading font-bold flex items-center justify-center gap-2">
                <Upload className="w-4 h-4" />
                {loading ? t("common.loading") : `Importer ${preview.total_rows} ligne(s)`}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — result */}
        {result && (
          <div className="p-5 space-y-3" data-testid="import-result">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded-lg bg-subtle">
                <div className="text-[10px] text-ink-muted uppercase">Lignes lues</div>
                <div className="font-heading font-bold text-lg text-ink">{result.total ?? 0}</div>
              </div>
              <div className="p-2 rounded-lg bg-[#2d8a56]/10">
                <div className="text-[10px] text-[#2d8a56] uppercase">Créés</div>
                <div className="font-heading font-bold text-lg text-[#2d8a56]">{result.created ?? 0}</div>
              </div>
              <div className="p-2 rounded-lg bg-brand/10">
                <div className="text-[10px] text-brand uppercase">Mis à jour</div>
                <div className="font-heading font-bold text-lg text-brand">{result.updated ?? 0}</div>
              </div>
            </div>
            {result.warnings?.length > 0 && (
              <div className="text-xs text-[#8a5a10] bg-[#e3a03b]/10 p-2 rounded-lg">
                <div className="font-semibold mb-1">⚠ Avertissements</div>
                <ul className="list-disc list-inside">{result.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
              </div>
            )}
            {result.errors?.length > 0 && (
              <div className="text-xs text-[#b12e31] bg-[#d34044]/10 p-2 rounded-lg">
                <div className="font-semibold mb-1">{t("stock.importErrors")}</div>
                <ul className="list-disc list-inside">{result.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}</ul>
                {result.errors.length > 10 && <div className="italic">…et {result.errors.length - 10} autres</div>}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={reset} className="flex-1 h-11 rounded-xl border border-[var(--border-default)] font-semibold">
                Importer un autre fichier
              </button>
              <button onClick={onClose} className="flex-1 h-11 rounded-xl bg-brand text-white font-semibold">
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FormDialog({ kind, form, setForm, products, onClose, onSubmit, t }) {
  const title = kind === "product" ? t("stock.newProduct")
              : kind === "lot" ? t("stock.newLot")
              : t("stock.newMovement");
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[var(--border-default)]">
          <h3 className="font-heading font-bold text-xl">{title}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={onSubmit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {kind === "product" && (
            <>
              <Field label={t("stock.productName")} required className="sm:col-span-2"><input required className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="f-name" /></Field>
              <Field label={t("stock.barcode")} className="sm:col-span-2"><input className={INPUT} value={form.barcode || ""} onChange={(e) => setForm({ ...form, barcode: e.target.value })} data-testid="f-barcode" /></Field>
              <Field label={t("stock.category")}><input className={INPUT} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="f-category" /></Field>
              <Field label={t("stock.unit")}><input className={INPUT} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="f-unit" /></Field>
              <Field label={t("stock.purchasePrice")}><input type="number" className={INPUT} value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} data-testid="f-purchase-price" /></Field>
              <Field label={t("stock.salePrice")}><input type="number" className={INPUT} value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="f-sale-price" /></Field>
              <Field label={t("stock.minThreshold")}><input type="number" className={INPUT} value={form.minimum_threshold} onChange={(e) => setForm({ ...form, minimum_threshold: e.target.value })} data-testid="f-threshold" /></Field>
            </>
          )}
          {kind === "lot" && (
            <>
              <Field label={t("stock.productName")} required className="sm:col-span-2">
                <select required className={INPUT} value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="lot-product">
                  <option value="">—</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
              <Field label={t("stock.batchNumber")} required><input required className={INPUT} value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} data-testid="lot-number" /></Field>
              <Field label={t("stock.expDate")} required><input type="date" required className={INPUT} value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} data-testid="lot-expiry" /></Field>
              <Field label={t("stock.supplier")}><input className={INPUT} value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} data-testid="lot-supplier" /></Field>
              <Field label={t("stock.quantity")} required><input type="number" required min={1} className={INPUT} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="lot-qty" /></Field>
            </>
          )}
          {kind === "movement" && (
            <>
              <Field label={t("stock.productName")} required className="sm:col-span-2">
                <select required className={INPUT} value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="mv-product">
                  <option value="">—</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name} (stock: {p.stock_quantity})</option>)}
                </select>
              </Field>
              <Field label={t("stock.type")} required>
                <select className={INPUT} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, subtype: e.target.value === "IN" ? "purchase" : "consumption" })} data-testid="mv-type">
                  <option value="IN">{t("stock.in")}</option>
                  <option value="OUT">{t("stock.out")}</option>
                </select>
              </Field>
              <Field label={t("stock.subtype")} required>
                <select className={INPUT} value={form.subtype} onChange={(e) => setForm({ ...form, subtype: e.target.value })} data-testid="mv-subtype">
                  {form.type === "IN" ? (
                    <>
                      <option value="purchase">{t("stock.purchase")}</option>
                      <option value="donation">{t("stock.donation")}</option>
                    </>
                  ) : (
                    <>
                      <option value="consumption">{t("stock.consumption")}</option>
                      <option value="internal">{t("stock.internal")}</option>
                      <option value="loss">{t("stock.loss")}</option>
                      <option value="expired">{t("stock.expired")}</option>
                      <option value="adjustment">{t("stock.adjustment")}</option>
                    </>
                  )}
                </select>
              </Field>
              <Field label={t("stock.quantity")} required><input type="number" required min={1} className={INPUT} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="mv-qty" /></Field>
              <Field label={t("stock.reason")} className="sm:col-span-2"><input className={INPUT} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="mv-reason" /></Field>
            </>
          )}
          <div className="sm:col-span-2 flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-12 rounded-xl border border-[var(--border-default)] font-semibold">{t("common.cancel")}</button>
            <button type="submit" data-testid={`${kind}-save`} className="flex-1 h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold">{t("common.save")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block mb-2 text-sm font-semibold text-ink">{label}{required && <span className="text-terracotta"> *</span>}</label>
      {children}
    </div>
  );
}
